import { Injectable } from '@angular/core';
import { AppConstants } from 'app/app.constants';
import {
  MobilePlatform,
  detectMobilePlatform,
} from 'app/utils/mobile-platform.utils';

const DISMISSED_FLAG_VALUE = 'true';

/**
 * Detects the visitor's mobile platform and sends them to the matching
 * IsoFarm app store listing.
 */
@Injectable({ providedIn: 'root' })
export class MobileAppService {
  /** Returns the visitor's mobile platform, or `null` on desktop. */
  getPlatform(): MobilePlatform | null {
    return detectMobilePlatform(
      navigator.userAgent,
      navigator.maxTouchPoints ?? 0,
    );
  }

  /** Returns the store listing URL for the given platform. */
  getStoreUrl(platform: MobilePlatform): string {
    if (platform === 'ios') {
      return AppConstants.MOBILE_APP.APP_STORE_URL;
    }
    return AppConstants.MOBILE_APP.GOOGLE_PLAY_URL;
  }

  /**
   * Whether the visitor already answered the "get the app" prompt on this
   * browser.
   *
   * @remarks The answer is stored per browser and never expires, so the
   * prompt is shown at most once per browser.
   */
  isPromptDismissed(): boolean {
    try {
      const key = AppConstants.KEYS_LOCAL_STORAGE.MOBILE_APP_PROMPT_DISMISSED;
      return localStorage.getItem(key) === DISMISSED_FLAG_VALUE;
    } catch {
      return false;
    }
  }

  /** Remembers on this browser that the visitor answered the prompt. */
  dismissPrompt(): void {
    try {
      const key = AppConstants.KEYS_LOCAL_STORAGE.MOBILE_APP_PROMPT_DISMISSED;
      localStorage.setItem(key, DISMISSED_FLAG_VALUE);
    } catch {
      // Storage may be blocked (e.g. private mode); the prompt simply shows again.
    }
  }

  /** Opens the store listing, keeping the current page in history. */
  openStore(platform: MobilePlatform): void {
    window.location.assign(this.getStoreUrl(platform));
  }

  /**
   * Replaces the current page with the store listing, so the back button
   * does not return to a page that would redirect again.
   */
  redirectToStore(platform: MobilePlatform): void {
    window.location.replace(this.getStoreUrl(platform));
  }
}
